import RegistrationNumber from 'marketplace/values/registration-number/registration-number';
import TechnicalBackgroundInfo from 'marketplace/values/technical-background-info/technical-background-info';
import InsuranceInfo, { InsuranceInfoJSON } from 'marketplace/values/insurance-info/insurance-info';
import BillingRateInfo, { BillingRateInfoJSON } from 'marketplace/values/billing-rate-info/billing-rate-info';
import EmailAddress from 'common/values/email-address/email-address';
import Guid from 'common/values/guid/guid';
import PhoneNumber from 'common/values/phone-number/phone-number';
import Address, { AddressJSON } from "common/values/address/address";

export interface IndividualProfileJSON {
  userId?: string;
  entityId?: string;
  entityName?: string;
  firstName: string;
  lastName: string;
  description?: string;
  location?: AddressJSON;
  phoneNumber?: string;
  avatarId?: string;
  email?: string;
  school?: string;
  degree?: string;
  resumeId?: string;
  professionalRegistration?: string;
  technicalBackground?: string;
  numberOfApplicationsDraftedProsecuted?: number;
  numberOfAllowedApplications?: number;
  barAdmissionYear?: number;
  isOnCall?: boolean;
  insured?: InsuranceInfoJSON;
  billingRate?: BillingRateInfoJSON;
  potentialDiversityCandidate?: boolean;
  collaborates?: boolean;
  additionalInfo?: string;
  categories?: string[];
  links?: string[];
  professionalPublications?: string[];
  sampleApplications?: string[];
  fluentLanguages?: string[];
  technicalLanguages?: string[];
  administrativeRegions?: string[];
  regionsRegistered?: string[];
  regionsLicensed?: string[];

  incomplete?: boolean;
}

export default class IndividualProfile {
  userId?: Guid;
  entityId?: Guid;
  entityName?: string;
  firstName: string;
  lastName: string;
  description?: string;
  location?: Address;
  phoneNumber?: PhoneNumber;
  avatarId?: Guid;
  email?: EmailAddress;
  school?: string;
  degree?: string;
  resumeId?: Guid;
  professionalRegistration?: RegistrationNumber;
  technicalBackground?: TechnicalBackgroundInfo;
  numberOfApplicationsDraftedProsecuted?: number;
  numberOfAllowedApplications?: number;
  barAdmissionYear?: number;
  isOnCall: boolean = false;
  insured?: InsuranceInfo;
  billingRate?: BillingRateInfo;
  potentialDiversityCandidate: boolean = false;
  collaborates: boolean = false;
  additionalInfo?: string;
  categories: string[] = [];
  links: string[] = [];
  professionalPublications: string[] = [];
  sampleApplications: string[] = [];
  fluentLanguages: string[] = [];
  technicalLanguages: string[] = [];
  administrativeRegions: string[] = [];
  regionsRegistered: string[] = [];
  regionsLicensed: string[] = [];

  incomplete: boolean = false;

  constructor(userId: Guid | undefined, firstName: string, lastName: string, incomplete?: boolean) {
    this.userId = userId;
    this.firstName = firstName;
    this.lastName = lastName;
    this.incomplete = incomplete ?? false;
  }

  clone(): IndividualProfile {
    const newProfile = new IndividualProfile(this.userId, this.firstName, this.lastName);
    const clonedProfile = Object.assign(newProfile, this);

    // Deep copy complex objects
    clonedProfile.location = this.location ? new Address(
      this.location.address1,
      this.location.address2,
      this.location.city,
      this.location.state,
      this.location.postalCode,
      this.location.country) : undefined;

    clonedProfile.phoneNumber = this.phoneNumber ? new PhoneNumber(
      this.phoneNumber.value
    ) : undefined;

    // Deep copy arrays
    clonedProfile.categories = this.categories ? [...this.categories] : [];
    clonedProfile.links = this.links ? [...this.links] : [];
    clonedProfile.professionalPublications = this.professionalPublications ? [...this.professionalPublications] : [];
    clonedProfile.sampleApplications = this.sampleApplications ? [...this.sampleApplications] : [];
    clonedProfile.fluentLanguages = this.fluentLanguages ? [...this.fluentLanguages] : [];
    clonedProfile.technicalLanguages = this.technicalLanguages ? [...this.technicalLanguages] : [];
    clonedProfile.administrativeRegions = this.administrativeRegions ? [...this.administrativeRegions] : [];
    clonedProfile.regionsRegistered = this.regionsRegistered ? [...this.regionsRegistered] : [];
    clonedProfile.regionsLicensed = this.regionsLicensed ? [...this.regionsLicensed] : [];

    return clonedProfile;
  }

  public static fromJSON(json: IndividualProfileJSON): IndividualProfile | undefined {
    if (!json) return;
    if (!json.firstName) {
      throw new Error("IndividualProfile.fromJSON: First Name is required");
    }
    if (!json.lastName) {
      throw new Error("IndividualProfile.fromJSON: Last Name is required");
    }

    const userId = json.userId ? Guid.fromJSON(json.userId) : undefined;

    const profile = new IndividualProfile(
      userId,
      json.firstName,
      json.lastName
    );

    profile.additionalInfo = json.additionalInfo;
    profile.administrativeRegions = json.administrativeRegions ?? [];
    profile.avatarId = json.avatarId ? Guid.fromJSON(json.avatarId) : undefined;
    profile.barAdmissionYear = json.barAdmissionYear;
    profile.billingRate = json.billingRate ? BillingRateInfo.fromJSON(json.billingRate) : undefined;
    profile.categories = json.categories ?? [];
    profile.collaborates = Boolean(json.collaborates);
    profile.description = json.description;
    profile.email = json.email ? EmailAddress.fromJSON(json.email) : undefined;
    profile.entityId = json.entityId ? Guid.fromJSON(json.entityId) : undefined;
    profile.entityName = json.entityName;
    profile.fluentLanguages = json.fluentLanguages ?? [];
    profile.insured = json.insured ? InsuranceInfo.fromJSON(json.insured) : undefined;
    profile.isOnCall = Boolean(json.isOnCall);
    profile.links = json.links ?? [];
    profile.location = json.location ? Address.fromJSON(json.location) : undefined;
    profile.numberOfAllowedApplications = json.numberOfAllowedApplications;
    profile.numberOfApplicationsDraftedProsecuted = json.numberOfApplicationsDraftedProsecuted;
    profile.phoneNumber = json.phoneNumber ? PhoneNumber.fromJSON(json.phoneNumber) : undefined;
    profile.potentialDiversityCandidate = Boolean(json.potentialDiversityCandidate);
    profile.professionalPublications = json.professionalPublications ?? [];
    profile.professionalRegistration = json.professionalRegistration ? RegistrationNumber.fromJSON(json.professionalRegistration) : undefined;
    profile.regionsLicensed = json.regionsLicensed ?? [];
    profile.regionsRegistered = json.regionsRegistered ?? [];
    profile.resumeId = json.resumeId ? Guid.fromJSON(json.resumeId) : undefined;
    profile.sampleApplications = json.sampleApplications ?? [];
    profile.school = json.school;
    profile.degree = json.degree;
    profile.technicalBackground = json.technicalBackground ? TechnicalBackgroundInfo.fromJSON(json.technicalBackground) : undefined;
    profile.technicalLanguages = json.technicalLanguages ?? [];

    return profile;
  }

  public toJSON(): IndividualProfileJSON {
    return {
      userId: this.userId?.toJSON(),
      entityId: this.entityId?.toJSON(),
      entityName: this.entityName,
      firstName: this.firstName,
      lastName: this.lastName,
      description: this.description,
      location: this.location,
      phoneNumber: this.phoneNumber?.toJSON(),
      avatarId: this.avatarId?.toJSON(),
      email: this.email?.toJSON(),
      school: this.school,
      degree: this.degree,
      resumeId: this.resumeId?.toJSON(),
      professionalRegistration: this.professionalRegistration?.toJSON(),
      technicalBackground: this.technicalBackground?.toJSON(),
      numberOfApplicationsDraftedProsecuted: this.numberOfApplicationsDraftedProsecuted,
      numberOfAllowedApplications: this.numberOfAllowedApplications,
      barAdmissionYear: this.barAdmissionYear,
      isOnCall: this.isOnCall,
      insured: this.insured?.toJSON(),
      billingRate: this.billingRate?.toJSON(),
      potentialDiversityCandidate: this.potentialDiversityCandidate,
      collaborates: this.collaborates,
      additionalInfo: this.additionalInfo,
      categories: this.categories,
      links: this.links,
      professionalPublications: this.professionalPublications,
      sampleApplications: this.sampleApplications,
      fluentLanguages: this.fluentLanguages,
      technicalLanguages: this.technicalLanguages,
      administrativeRegions: this.administrativeRegions,
      regionsRegistered: this.regionsRegistered,
      regionsLicensed: this.regionsLicensed,
    };
  }

  public getFullName(): string { 
    return `${this.firstName} ${this.lastName}`; 
  }
}
